import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  generateTeachingProgram,
  pollJobStatus as pollProgramJobStatus,
  TeachingProgramFormData,
} from 'src/services/teaching-programs.service';
import { WorksheetService } from 'src/services/worksheet.service';
import { SlideshowService } from 'src/services/slideshow.service';
import { SlideshowFormData } from 'src/models/slideshow.types';
import { fileToBase64 } from 'src/services/slides.service';
import {
  Differentiation,
  QuestionType,
  WorksheetDocument,
  WorksheetFormData,
  WorksheetJobStatus,
  WorksheetTemplate,
} from 'src/models/worksheet.types';
import { useUsage } from 'src/hooks/useUsage';
import { useDocumentStorage } from 'src/hooks/useDocumentStorage';
import { UsageIndicator } from 'src/components/common/UsageIndicator';
import { usePublicTrial } from 'src/contexts/PublicTrialContext';
import { useProductFeatures } from 'src/hooks/useProductFeatures';
import { MissingFieldsSummary, MissingField } from 'src/components/common/MissingFieldsSummary';
import { getLogger } from 'src/utils/logger';
// The slides section reuses the template-grid / option-card styles defined in
// this stylesheet. It previously reached the bundle via SlidesForm, but this
// flow no longer pulls SlidesForm into its import graph, so import it directly.
import 'src/styles/components/forms/slides.css';

const logger = getLogger('LessonCreatorFlow');

// A request can be aborted two ways once the shared controller fires: the poll
// sleep rejects with a DOMException('AbortError'), while an in-flight Axios call
// rejects with its own CanceledError (code 'ERR_CANCELED'). Treat both as a
// silent cancel so unmounting mid-generation never surfaces a false error.
const isAbortError = (err: unknown): boolean =>
  (err instanceof DOMException && err.name === 'AbortError') ||
  (err as { name?: string; code?: string })?.name === 'CanceledError' ||
  (err as { name?: string; code?: string })?.code === 'ERR_CANCELED';

export type LessonResource = 'teaching_program' | 'worksheet' | 'slides';

// Where a slides-only flow gets its source lesson from. Uploading is the
// default; describing it hands the same questions the lesson-plan form asks to
// the backend, which writes the lesson plan itself and returns only the deck.
type SlidesSource = 'upload' | 'describe';

export interface LessonCreatorFlowProps {
  initialTopic: string;
  initialResources: LessonResource[];
  onBackToDashboard: () => void;
}

// Each generated artefact tracks its own progress so the worksheet and
// slideshow — which run concurrently once the teaching program is ready — can
// report independently rather than sharing one linear bar.
type JobKey = 'program' | 'worksheet' | 'slides';

interface JobProgressState {
  label: string;
  status: 'running' | 'done' | 'error';
  percent: number;
  message: string;
}

// ── Curriculum data ──────────────────────────────────────────────────────────

const NSW_SUBJECTS = [
  { value: 'english-component-a', label: 'English - Component A' },
  { value: 'english-component-b', label: 'English - Component B' },
  { value: 'mathematics', label: 'Mathematics' },
  { value: 'science-technology', label: 'Science and Technology' },
  { value: 'human-society-environment', label: 'Human Society and its Environment' },
  { value: 'creative-arts', label: 'Creative Arts' },
  { value: 'personal-development-health', label: 'Personal Development and Health' },
  { value: 'physical-education', label: 'Physical Education' },
];

const AUSTRALIAN_SUBJECTS = [
  { value: 'english', label: 'English' },
  { value: 'health_physical', label: 'Health and Physical Education' },
  { value: 'hass', label: 'Humanities and Social Sciences' },
  { value: 'math', label: 'Mathematics' },
  { value: 'science', label: 'Science' },
  { value: 'design_tech', label: 'Design and Technologies' },
  { value: 'digital_tech', label: 'Digital Technologies' },
  { value: 'dance', label: 'Dance' },
  { value: 'drama', label: 'Drama' },
  { value: 'media_arts', label: 'Media Arts' },
  { value: 'music', label: 'Music' },
  { value: 'visual_arts', label: 'Visual Arts' },
];

const VIC_SUBJECTS = [
  { value: 'mathematics', label: 'Mathematics' },
  { value: 'english', label: 'English' },
  { value: 'science', label: 'Science' },
  { value: 'humanities', label: 'Humanities' },
  { value: 'arts', label: 'The Arts' },
  { value: 'health_physical', label: 'Health and Physical Education' },
  { value: 'technology', label: 'Technologies' },
];

const NSW_STAGES = [
  { value: 'early_stage_1', label: 'Early Stage 1' },
  { value: 'stage_1', label: 'Stage 1' },
  { value: 'stage_2', label: 'Stage 2' },
  { value: 'stage_3', label: 'Stage 3' },
];

const YEAR_LEVELS = [
  { value: 'foundation', label: 'Foundation' },
  { value: 'year_1', label: 'Year 1' },
  { value: 'year_2', label: 'Year 2' },
  { value: 'year_3', label: 'Year 3' },
  { value: 'year_4', label: 'Year 4' },
  { value: 'year_5', label: 'Year 5' },
  { value: 'year_6', label: 'Year 6' },
];

const VIC_CAPABILITIES = [
  { value: 'critical_thinking', label: 'Critical and Creative Thinking' },
  { value: 'ethics', label: 'Ethical Capability' },
  { value: 'intercultural', label: 'Intercultural Capability' },
  { value: 'personal', label: 'Personal and Social Capability' },
];

const VIC_PRIORITIES = [
  { value: 'aboriginal_torres_strait_islander', label: 'Aboriginal and Torres Strait Islander Histories and Cultures' },
  { value: 'asia_australias_engagement', label: "Asia and Australia's Engagement with Asia" },
  { value: 'sustainability', label: 'Sustainability' },
];

// ── Unit of work constants ────────────────────────────────────────────────────

type LessonTableOrientation = 'columns' | 'rows';

const LESSON_TABLE_ORIENTATIONS: {
  value: LessonTableOrientation;
  title: string;
  description: string;
  glyph: React.ReactNode;
}[] = [
  {
    value: 'columns',
    title: 'Grid view',
    description: 'View lessons side by side in columns',
    glyph: (
      <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="16" rx="1.5" />
        <path d="M9 4v16M15 4v16" />
      </svg>
    ),
  },
  {
    value: 'rows',
    title: 'One by one',
    description: 'View one lesson per page',
    glyph: (
      <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="18" height="5" rx="1.5" />
        <rect x="3" y="9.5" width="18" height="5" rx="1.5" />
        <rect x="3" y="16" width="18" height="5" rx="1.5" />
      </svg>
    ),
  },
];

// ── Worksheet constants ───────────────────────────────────────────────────────

const WORKSHEET_TYPES = [
  { value: WorksheetTemplate.LESSON_STARTER, label: 'Lesson starter', hint: '~5 min' },
  { value: WorksheetTemplate.LESSON_ACTIVITY, label: 'Lesson activity', hint: '~20 min' },
  { value: WorksheetTemplate.LESSON_RECAP, label: 'Lesson recap', hint: '~10 min' },
  { value: WorksheetTemplate.KNOWLEDGE_CHECK, label: 'Knowledge check', hint: '~15 min' },
  { value: WorksheetTemplate.HOMEWORK, label: 'Homework', hint: '~20 min' },
  { value: WorksheetTemplate.ASSESSMENT, label: 'Assessment', hint: '~30 min' },
];

interface QuestionPreset {
  label: string;
  styles: QuestionType[];
  counts: Partial<Record<QuestionType, number>>;
}

const QUESTION_PRESETS: QuestionPreset[] = [
  {
    label: 'Gradual release',
    styles: [QuestionType.MULTIPLE_CHOICE, QuestionType.SHORT_ANSWER, QuestionType.FILL_IN_BLANK, QuestionType.LONG_ANSWER],
    counts: { [QuestionType.MULTIPLE_CHOICE]: 3, [QuestionType.SHORT_ANSWER]: 3, [QuestionType.FILL_IN_BLANK]: 1, [QuestionType.LONG_ANSWER]: 1 },
  },
  {
    label: 'Quick check',
    styles: [QuestionType.TRUE_FALSE, QuestionType.MULTIPLE_CHOICE, QuestionType.MATCHING, QuestionType.FILL_IN_BLANK],
    counts: { [QuestionType.TRUE_FALSE]: 2, [QuestionType.MULTIPLE_CHOICE]: 3, [QuestionType.MATCHING]: 1, [QuestionType.FILL_IN_BLANK]: 1 },
  },
  {
    label: 'Writing focus',
    styles: [QuestionType.SHORT_ANSWER, QuestionType.LONG_ANSWER, QuestionType.FILL_IN_BLANK],
    counts: { [QuestionType.SHORT_ANSWER]: 3, [QuestionType.LONG_ANSWER]: 2, [QuestionType.FILL_IN_BLANK]: 2 },
  },
];

const QUESTION_STYLES = [
  { value: QuestionType.TRUE_FALSE, label: 'True / false', maxQuestions: 10 },
  { value: QuestionType.MULTIPLE_CHOICE, label: 'Multiple choice', maxQuestions: 15 },
  { value: QuestionType.MATCHING, label: 'Matching', maxQuestions: 6 },
  { value: QuestionType.FILL_IN_BLANK, label: 'Fill in the blank', maxQuestions: 10 },
  { value: QuestionType.SHORT_ANSWER, label: 'Short answer', maxQuestions: 8 },
  { value: QuestionType.DRAWING, label: 'Drawing', maxQuestions: 4 },
  { value: QuestionType.LONG_ANSWER, label: 'Long answer', maxQuestions: 5 },
];

const DIFFERENTIATION_OPTIONS = [
  { value: Differentiation.SUPPORT, label: 'Support', description: 'Scaffolded questions with simpler vocabulary and extra prompts.', featureKey: 'differentiation_support' },
  { value: Differentiation.CORE, label: 'Core', description: 'Standard expectations for the selected stage.' },
  { value: Differentiation.CHALLENGE, label: 'Challenge', description: 'Extended tasks with higher-order, open-ended thinking.', featureKey: 'differentiation_challenge' },
];

// ── Slides constants ──────────────────────────────────────────────────────────

const SLIDE_TEMPLATES = [
  { id: 'gymea_pink', name: 'Gymea Pink', previewImage: '/images/thumbnail_gymea_pink.jpg' },
  { id: 'gymea_green', name: 'Gymea Green', previewImage: '/images/thumbnail_gymea_green.png' },
  { id: 'gymea_light', name: 'Gymea Light', previewImage: '/images/thumbnail_gymea_light.png' },
  { id: 'boho_baby', name: 'Boho Baby', previewImage: '/images/thumbnail_bohobaby.png' },
  { id: 'in_the_sky', name: 'In the Sky', previewImage: '/images/thumbnail_inthesky.png' },
  { id: 'literacy_lovers', name: 'Literacy Lovers', previewImage: '/images/thumbnail_literacylovers.png' },
  { id: 'maths_masters', name: 'Maths Masters', previewImage: '/images/thumbnail_mathmasters.png' },
  { id: 'native_bouquet', name: 'Native Bouquet', previewImage: '/images/thumbnail_nativebouquet.png' },
  { id: 'sleepy_koala', name: 'Sleepy Koala', previewImage: '/images/thumbnail_sleepykoala.png' },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

const downloadWorksheetDocument = (doc: WorksheetDocument) => {
  if (doc.docBase64) {
    const bytes = Uint8Array.from(atob(doc.docBase64), (c) => c.charCodeAt(0));
    const blob = new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `worksheet-${doc.differentiationLevel}-${doc.documentId}.docx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } else if (doc.downloadUrl) {
    const link = document.createElement('a');
    link.href = doc.downloadUrl;
    link.download = `worksheet-${doc.differentiationLevel}-${doc.documentId}.docx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

// ── Component ─────────────────────────────────────────────────────────────────

export const LessonCreatorFlow: React.FC<LessonCreatorFlowProps> = ({
  initialTopic,
  initialResources,
  onBackToDashboard,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const { isPublicTrial, requestTrialSignup } = usePublicTrial();
  const { getUsageForProduct, refresh: refreshUsage, loading: usageLoading } = useUsage();
  const { refresh: refreshDocuments } = useDocumentStorage();
  const { canUseFeature } = useProductFeatures('worksheet');
  const { canUseFeature: canUseUowFeature, loading: uowFeaturesLoading } = useProductFeatures('unit_of_work');

  // ── Shared form state ──────────────────────────────────────────────────────
  const [curriculum, setCurriculum] = useState<'nsw' | 'australian' | 'vic' | null>(null);
  const [subject, setSubject] = useState('');
  const [stages, setStages] = useState<string[]>([]);
  const [yearLevels, setYearLevels] = useState<string[]>([]);
  const [capabilities, setCapabilities] = useState<string[]>([]);
  const [crossCurriculumPriorities, setCrossCurriculumPriorities] = useState<string[]>([]);
  const [outcomes, setOutcomes] = useState(initialTopic);
  const [additionalInfo, setAdditionalInfo] = useState('');

  // ── Teaching program state ─────────────────────────────────────────────────
  const [programType, setProgramType] = useState<'lesson_plan' | 'unit_of_work'>('lesson_plan');
  const [numberOfLessons, setNumberOfLessons] = useState<number | undefined>(undefined);
  // null until the teacher picks a layout, so the curriculum default keeps
  // applying while they switch curriculums.
  const [tableOrientation, setTableOrientation] = useState<LessonTableOrientation | null>(null);

  // Units of work run to 10 lessons on Gymea PRO and 5 on every other tier.
  // The public trial previews the full range; the backend clamps regardless.
  const canUseTenLessons = isPublicTrial || canUseUowFeature('lesson_count_10');
  const maxLessons = canUseTenLessons ? 10 : 5;

  // VIC's unit-of-work template runs lessons down the page, every other
  // curriculum runs them across it. Derived rather than stored so a curriculum
  // change re-defaults the picker until the teacher overrides it.
  const effectiveTableOrientation: LessonTableOrientation =
    tableOrientation ?? (curriculum === 'vic' ? 'rows' : 'columns');

  // Force unit_of_work for VIC
  useEffect(() => {
    if (curriculum === 'vic' && programType !== 'unit_of_work') {
      setProgramType('unit_of_work');
    }
  }, [curriculum, programType]);

  // Pull a lesson count above the PRO ceiling back down once feature access is known.
  useEffect(() => {
    if (uowFeaturesLoading || isPublicTrial) {
      return;
    }
    setNumberOfLessons((prev) => (prev !== undefined && prev > maxLessons ? maxLessons : prev));
  }, [uowFeaturesLoading, isPublicTrial, maxLessons]);

  // ── Worksheet state ────────────────────────────────────────────────────────
  const [worksheetType, setWorksheetType] = useState<WorksheetTemplate>(WorksheetTemplate.LESSON_ACTIVITY);
  const [questionStyles, setQuestionStyles] = useState<QuestionType[]>([...QUESTION_PRESETS[0].styles]);
  const [questionCounts, setQuestionCounts] = useState<Partial<Record<QuestionType, number>>>({ ...QUESTION_PRESETS[0].counts });
  const [differentiationLevels, setDifferentiationLevels] = useState<Differentiation[]>([Differentiation.CORE]);
  const [includeAnswerKey, setIncludeAnswerKey] = useState(true);

  // ── Slides state ───────────────────────────────────────────────────────────
  const [selectedTemplate, setSelectedTemplate] = useState(SLIDE_TEMPLATES[0].id);
  const [imageSource, setImageSource] = useState<'generative' | 'none'>('generative');
  const [slidesInstructions, setSlidesInstructions] = useState('');
  const [uploadedSlidesFile, setUploadedSlidesFile] = useState<{ name: string; docBase64: string } | null>(null);
  const [isSlidesFileDragging, setIsSlidesFileDragging] = useState(false);
  const [slidesSource, setSlidesSource] = useState<SlidesSource>('upload');
  const slidesFileInputRef = useRef<HTMLInputElement>(null);

  // ── Generation state ───────────────────────────────────────────────────────
  const [isGenerating, setIsGenerating] = useState(false);
  const [jobProgress, setJobProgress] = useState<Partial<Record<JobKey, JobProgressState>>>({});
  const [error, setError] = useState<string | null>(null);

  // ── Results state ──────────────────────────────────────────────────────────
  const [programResult, setProgramResult] = useState<{
    documentId: string; docBase64: string; documentType: string;
  } | null>(null);
  const [worksheetResults, setWorksheetResults] = useState<WorksheetDocument[]>([]);
  const [slidesResult, setSlidesResult] = useState<{
    documentId: string; downloadUrl: string;
  } | null>(null);
  const [isComplete, setIsComplete] = useState(false);
  // Non-fatal failures from the parallel follow-on phase (e.g. the slideshow
  // failed but the worksheet succeeded). Surfaced on the results screen.
  const [followOnErrors, setFollowOnErrors] = useState<string[]>([]);

  const pollAbortRef = useRef<AbortController | null>(null);
  useEffect(() => () => pollAbortRef.current?.abort(), []);

  // Merge a partial update into one job's progress entry without disturbing the
  // others — lets the concurrent worksheet and slideshow jobs report
  // independently into the same panel.
  const updateJob = (key: JobKey, patch: Partial<JobProgressState>) =>
    setJobProgress((prev) => ({
      ...prev,
      [key]: { ...(prev[key] as JobProgressState), ...patch },
    }));

  // ── Derived values ─────────────────────────────────────────────────────────
  const hasTeachingProgram = initialResources.includes('teaching_program');
  const hasWorksheet = initialResources.includes('worksheet');
  const hasSlides = initialResources.includes('slides');
  // A slides-only flow needs a source lesson, either uploaded or described.
  const slidesPicksSource = hasSlides && !hasTeachingProgram;
  const slidesNeedsUpload = slidesPicksSource && slidesSource === 'upload';
  const slidesNeedsBrief = slidesPicksSource && slidesSource === 'describe';
  // Curriculum / subject / stages / outcomes drive teaching-program and
  // worksheet generation, and the lesson the backend writes behind a described
  // slideshow. An uploaded document carries all of it already, so for that flow
  // the shared fields stay hidden and unrequired.
  const needsLessonContext = hasTeachingProgram || hasWorksheet || slidesNeedsBrief;

  const isVIC = curriculum === 'vic';
  const isNSW = curriculum === 'nsw';

  const availableSubjects = isNSW ? NSW_SUBJECTS : isVIC ? VIC_SUBJECTS : AUSTRALIAN_SUBJECTS;
  const availableStages = isNSW ? NSW_STAGES : YEAR_LEVELS;
  const stageLabel = isNSW ? 'stage(s)' : 'year level(s)';
  const activeStages = isVIC ? yearLevels : stages;

  const lessonPlanUsage = getUsageForProduct('lesson_plan');
  const unitOfWorkUsage = getUsageForProduct('unit_of_work');
  const worksheetUsage = getUsageForProduct('worksheet');
  const slideshowUsage = getUsageForProduct('slideshow_generative');
  const slideshowNoImageUsage = getUsageForProduct('slideshow_no_image');

  // Australian-format month label for the usage indicators (matches the other
  // document forms, e.g. "June 2026"). Memoized so it isn't recomputed on every
  // re-render of this large form (typing a field, toggling a checkbox, etc.).
  const usagePeriod = useMemo(
    () => new Date().toLocaleDateString('en-AU', { year: 'numeric', month: 'long' }),
    [],
  );

  // ── Curriculum change resets ───────────────────────────────────────────────
  const handleCurriculumChange = (value: 'nsw' | 'australian' | 'vic') => {
    setCurriculum(value);
    setSubject('');
    setStages([]);
    setYearLevels([]);
    setCapabilities([]);
    setCrossCurriculumPriorities([]);
  };

  const toggleStage = (value: string) => {
    if (isVIC) {
      setYearLevels((prev) =>
        prev.includes(value) ? prev.filter((s) => s !== value) : [...prev, value]
      );
    } else {
      setStages((prev) =>
        prev.includes(value) ? prev.filter((s) => s !== value) : [...prev, value]
      );
    }
  };

  // ── Worksheet helpers ──────────────────────────────────────────────────────
  const handleWorksheetTypeChange = (type: WorksheetTemplate) => {
    setWorksheetType(type);
    const quickCheck = QUESTION_PRESETS[1];
    const gradualRelease = QUESTION_PRESETS[0];
    const isCurrentlyQuickCheck =
      quickCheck.styles.length === questionStyles.length &&
      quickCheck.styles.every((s) => questionStyles.includes(s)) &&
      quickCheck.styles.every((s) => questionCounts[s] === quickCheck.counts[s]);

    if (type === WorksheetTemplate.KNOWLEDGE_CHECK) {
      setQuestionStyles([...quickCheck.styles]);
      setQuestionCounts({ ...quickCheck.counts });
    } else if (isCurrentlyQuickCheck) {
      setQuestionStyles([...gradualRelease.styles]);
      setQuestionCounts({ ...gradualRelease.counts });
    }
  };

  const toggleQuestionStyle = (value: QuestionType) => {
    const removing = questionStyles.includes(value);
    setQuestionStyles((prev) => (removing ? prev.filter((s) => s !== value) : [...prev, value]));
    setQuestionCounts((prev) => {
      const next = { ...prev };
      if (removing) {
        delete next[value];
      } else {
        next[value] = 1;
      }
      return next;
    });
  };

  const toggleDifferentiation = (value: Differentiation, featureKey?: string) => {
    if (featureKey && !isPublicTrial && !canUseFeature(featureKey)) return;
    setDifferentiationLevels((prev) =>
      prev.includes(value) ? prev.filter((d) => d !== value) : [...prev, value]
    );
  };

  // ── Slides file upload ─────────────────────────────────────────────────────
  const handleSlidesFileSelect = (file: File) => {
    if (!file.name.endsWith('.docx')) {
      setError('Please upload a .docx Word document.');
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const base64 = (e.target?.result as string).split(',')[1];
      setUploadedSlidesFile({ name: file.name, docBase64: base64 });
      setError(null);
    };
    reader.readAsDataURL(file);
  };

  // ── Validation ─────────────────────────────────────────────────────────────
  const getMissingFields = (): MissingField[] => {
    const items: MissingField[] = [];

    if (needsLessonContext) {
      if (!curriculum) items.push({ label: 'Choose a curriculum', anchorId: 'lc-curriculum' });
      if (!subject) items.push({ label: 'Choose a subject', anchorId: 'lc-subject' });
      if (activeStages.length === 0) items.push({ label: `Choose at least one ${stageLabel.replace(/\(s\)/g, '')}`, anchorId: 'lc-stages' });

      if (isVIC) {
        const capCount = capabilities.length;
        if (capCount < 1 || capCount > 4) items.push({ label: 'Choose 1–4 general capabilities', anchorId: 'lc-capabilities' });
        const priCount = crossCurriculumPriorities.length;
        if (priCount < 1 || priCount > 3) items.push({ label: 'Choose 1–3 cross-curriculum priorities', anchorId: 'lc-priorities' });
      }

      if (outcomes.trim() === '') items.push({ label: 'Describe what you want to teach', anchorId: 'lc-outcomes' });
    }

    if (hasTeachingProgram && programType === 'unit_of_work') {
      if (!numberOfLessons || numberOfLessons <= 0) {
        items.push({ label: 'Set the number of lessons', anchorId: 'lc-num-lessons' });
      } else if (numberOfLessons > maxLessons) {
        items.push({ label: `Reduce the number of lessons (max ${maxLessons})`, anchorId: 'lc-num-lessons' });
      }
    }

    if (hasWorksheet) {
      if (questionStyles.length === 0) items.push({ label: 'Choose at least one question style', anchorId: 'lc-question-styles' });
      const hasInvalidCounts = questionStyles.some((s) => !questionCounts[s] || (questionCounts[s] ?? 0) <= 0);
      if (hasInvalidCounts) items.push({ label: 'Set question counts for each selected style', anchorId: 'lc-question-styles' });
      if (differentiationLevels.length === 0) items.push({ label: 'Choose at least one differentiation level', anchorId: 'lc-differentiation' });
    }

    if (slidesNeedsUpload && !uploadedSlidesFile) {
      items.push({ label: 'Upload a lesson plan for your slideshow', anchorId: 'lc-slides-upload' });
    }

    return items;
  };

  // ── Generation ─────────────────────────────────────────────────────────────
  const handleGenerate = async () => {
    if (needsLessonContext && !curriculum) return;
    setError(null);
    setFollowOnErrors([]);
    // An earlier run's documents are deliberately left in place here. Each job
    // overwrites its own result once it succeeds, and anything that fails this
    // run is cleared at the commit point below — so a regenerate that falls
    // over entirely returns to the form with those documents still reachable.

    if (isPublicTrial) {
      requestTrialSignup();
      return;
    }

    try {
      setIsGenerating(true);

      // Seed the progress panel so every requested artefact is visible up
      // front — the worksheet/slideshow rows sit "waiting" until the teaching
      // program (their source) is ready, then run together.
      const seeded: Partial<Record<JobKey, JobProgressState>> = {};
      if (hasTeachingProgram) {
        seeded.program = {
          label: programType === 'lesson_plan' ? 'Lesson plan' : 'Unit of work',
          status: 'running',
          percent: 5,
          message: 'Generating your teaching program…',
        };
      }
      const followOnWaitMsg = hasTeachingProgram
        ? 'Waiting for your teaching program…'
        : 'Starting…';
      if (hasWorksheet) {
        seeded.worksheet = { label: 'Worksheet', status: 'running', percent: 0, message: followOnWaitMsg };
      }
      if (hasSlides) {
        seeded.slides = { label: 'Slideshow', status: 'running', percent: 0, message: followOnWaitMsg };
      }
      setJobProgress(seeded);

      let generatedDocBase64: string | null = null;
      let generatedDocumentId: string | null = null;

      // ── Step 1: Teaching program ─────────────────────────────────────────
      // Must finish first — both the worksheet and the slideshow read its
      // documentId / structured content, so there's a genuine ordering
      // dependency here (unlike between the two follow-ons).
      if (hasTeachingProgram) {
        const payload: TeachingProgramFormData = {
          programType,
          curriculum: curriculum!,
          subject,
          stages: isVIC ? undefined : stages,
          yearLevels: isVIC ? yearLevels : undefined,
          outcomes,
          numberOfLessons: programType === 'unit_of_work' ? numberOfLessons : undefined,
          lessonTableOrientation: programType === 'unit_of_work' ? effectiveTableOrientation : undefined,
          additionalInfo: additionalInfo || undefined,
          capabilities: isVIC ? capabilities : undefined,
          crossCurriculumPriorities: isVIC ? crossCurriculumPriorities : undefined,
        };

        const accessToken = await getAccessTokenSilently();
        const jobRes = await generateTeachingProgram(payload, accessToken);
        if (!jobRes.data || jobRes.error) {
          throw new Error(jobRes.error?.message || 'Failed to start teaching program generation');
        }

        const statusRes = await pollProgramJobStatus(
          jobRes.data.job_id,
          accessToken,
          ({ percentage, message }) =>
            updateJob('program', {
              percent: Math.round(percentage),
              message: message || 'Generating teaching program…',
            })
        );

        if (!statusRes.data || statusRes.error) {
          throw new Error(statusRes.error?.message || 'Failed to generate teaching program');
        }
        if (statusRes.data.status === 'failed') {
          throw new Error(statusRes.data.error || 'Teaching program generation failed');
        }

        generatedDocBase64 = statusRes.data.doc_base64 ?? null;
        generatedDocumentId = statusRes.data.document_id ?? null;

        setProgramResult({
          documentId: generatedDocumentId ?? '',
          docBase64: generatedDocBase64 ?? '',
          documentType: programType,
        });
        refreshUsage();
        // Refresh the saved-documents library so the new program shows on /documents
        refreshDocuments();
        updateJob('program', { status: 'done', percent: 100, message: 'Ready' });
      }

      // ── Steps 2 & 3: Worksheet + slideshow run CONCURRENTLY ──────────────
      // They share only the teaching program's documentId, so neither depends
      // on the other — fire both and await together. A single shared abort
      // controller stops both polls on unmount; allSettled + per-job error
      // capture means one failing never aborts the other.
      pollAbortRef.current?.abort();
      const controller = new AbortController();
      pollAbortRef.current = controller;

      const followOnToken =
        hasWorksheet || hasSlides ? await getAccessTokenSilently() : '';

      // Each runner resolves to null on success, or an error string on
      // failure, so the caller can report partial results without one job's
      // failure rejecting the whole batch.
      const runWorksheet = async (): Promise<string | null> => {
        try {
          updateJob('worksheet', { message: 'Generating your worksheet…' });
          const source = generatedDocBase64 && generatedDocumentId
            ? {
                type: programType as 'lesson_plan' | 'unit_of_work',
                documentId: generatedDocumentId,
                docBase64: generatedDocBase64,
                lessonScope: 'whole_unit' as const,
              }
            : undefined;

          const wsPayload: WorksheetFormData = {
            curriculum: curriculum!,
            subject,
            stages: isVIC ? yearLevels : stages,
            worksheetType,
            outcomes,
            additionalInfo: additionalInfo || undefined,
            questionStyles,
            questionCounts,
            differentiationLevels: differentiationLevels.length > 0 ? differentiationLevels : [Differentiation.CORE],
            includeAnswerKey,
            source,
          };

          const wsJobRes = await WorksheetService.generateWorksheet(wsPayload, followOnToken);
          const wsStatus = await WorksheetService.pollJobStatus(
            wsJobRes.job_id,
            followOnToken,
            ({ percentage, message }) =>
              updateJob('worksheet', {
                percent: Math.round(percentage),
                message: message || 'Generating worksheet…',
              }),
            undefined,
            undefined,
            controller.signal
          );

          if (wsStatus.status !== WorksheetJobStatus.COMPLETED) {
            throw new Error(wsStatus.error?.message || 'Worksheet generation failed');
          }
          setWorksheetResults(wsStatus.documents ?? []);
          refreshUsage();
          refreshDocuments();
          updateJob('worksheet', { status: 'done', percent: 100, message: 'Ready' });
          return null;
        } catch (err) {
          if (isAbortError(err)) return null;
          const msg = err instanceof Error ? err.message : 'Worksheet generation failed';
          updateJob('worksheet', { status: 'error', message: msg });
          return `Worksheet: ${msg}`;
        }
      };

      const runSlides = async (): Promise<string | null> => {
        try {
          updateJob('slides', { message: 'Generating your slideshow…' });

          // Described lessons send the brief instead of a document: the backend
          // writes the lesson plan behind the deck and returns only the slides.
          let slidesSourceFields: Pick<SlideshowFormData, 'docBase64' | 'lessonBrief' | 'documentId'>;
          if (slidesNeedsBrief) {
            slidesSourceFields = {
              lessonBrief: {
                curriculum: curriculum!,
                subject,
                outcomes,
                stages: isVIC ? undefined : stages,
                yearLevels: isVIC ? yearLevels : undefined,
                capabilities: isVIC ? capabilities : undefined,
                crossCurriculumPriorities: isVIC ? crossCurriculumPriorities : undefined,
                additionalInfo: additionalInfo || undefined,
              },
            };
          } else {
            let slideDocBase64 = generatedDocBase64;
            if (!slideDocBase64 && uploadedSlidesFile) {
              slideDocBase64 = uploadedSlidesFile.docBase64;
            }
            if (!slideDocBase64 && slidesFileInputRef.current?.files?.[0]) {
              slideDocBase64 = await fileToBase64(slidesFileInputRef.current.files[0]);
            }
            if (!slideDocBase64) throw new Error('No document available for slideshow');
            slidesSourceFields = {
              docBase64: slideDocBase64,
              documentId: generatedDocumentId ?? undefined,
            };
          }

          const slidesJobRes = await SlideshowService.generateSlideshow(
            {
              ...slidesSourceFields,
              additionalInstructions: slidesInstructions.trim() || undefined,
              templateName: selectedTemplate,
              imageSource,
            },
            followOnToken
          );

          const slidesStatus = await SlideshowService.pollJobStatus(
            slidesJobRes.job_id,
            followOnToken,
            ({ percentage, message }) =>
              updateJob('slides', {
                percent: Math.round(percentage),
                message: message || 'Generating slideshow…',
              }),
            undefined,
            undefined,
            controller.signal
          );

          if (slidesStatus.status !== 'completed') {
            throw new Error(slidesStatus.error || 'Slideshow generation failed');
          }
          setSlidesResult({
            documentId: slidesStatus.document_id ?? '',
            downloadUrl: slidesStatus.download_url ?? '',
          });
          refreshUsage();
          refreshDocuments();
          updateJob('slides', { status: 'done', percent: 100, message: 'Ready' });
          return null;
        } catch (err) {
          if (isAbortError(err)) return null;
          const msg = err instanceof Error ? err.message : 'Slideshow generation failed';
          updateJob('slides', { status: 'error', message: msg });
          return `Slideshow: ${msg}`;
        }
      };

      // Settle both follow-ons in fixed slots so each failure stays tied to the
      // job it came from — the results below are cleared per job, which an
      // index-shifting array of only the requested tasks can't tell apart.
      const settled = await Promise.allSettled([
        hasWorksheet ? runWorksheet() : Promise.resolve(null),
        hasSlides ? runSlides() : Promise.resolve(null),
      ]);
      const settledError = (s: PromiseSettledResult<string | null>): string | null =>
        s.status === 'fulfilled' ? s.value : s.reason?.message ?? 'Generation failed';
      const [worksheetError, slidesError] = settled.map(settledError);

      const followOnFailures = [worksheetError, slidesError].filter((v): v is string => Boolean(v));
      const followOnCount = (hasWorksheet ? 1 : 0) + (hasSlides ? 1 : 0);

      // The teaching program (if any) is already a finished result, so we land
      // on the results screen whenever anything succeeded. Only a total
      // failure with nothing to show keeps the user on the form to retry —
      // with the previous run's documents intact, so the resume banner can
      // still offer them back.
      const producedSomething =
        hasTeachingProgram || followOnFailures.length < followOnCount;
      if (!producedSomething) {
        setError(followOnFailures.join(' '));
        return;
      }

      // Drop a previous run's document only for a job that actually failed this
      // run: a stale worksheet must not sit on the results screen next to a
      // freshly generated program. The teaching program needs no equivalent —
      // its failure throws, so it never reaches here.
      if (worksheetError) setWorksheetResults([]);
      if (slidesError) setSlidesResult(null);

      setFollowOnErrors(followOnFailures);
      setIsComplete(true);
    } catch (err) {
      logger.error('Lesson creator generation error:', err);
      setError(err instanceof Error ? err.message : 'Generation failed. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  // ── Results downloads ──────────────────────────────────────────────────────
  const downloadProgramDocument = () => {
    if (!programResult?.docBase64) return;
    const bytes = Uint8Array.from(atob(programResult.docBase64), (c) => c.charCodeAt(0));
    const blob = new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const baseName = programResult.documentType === 'lesson_plan' ? 'lesson-plan' : 'unit-of-work';
    link.download = `${baseName}-${programResult.documentId}.docx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const downloadSlidesDocument = () => {
    if (!slidesResult?.downloadUrl) return;
    const link = document.createElement('a');
    link.href = slidesResult.downloadUrl;
    link.download = `slideshow-${slidesResult.documentId}.pptx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Trigger each download with a small stagger — browsers throttle or block
  // several downloads fired in the same tick.
  const downloadAllResults = () => {
    const jobs: Array<() => void> = [];
    if (programResult) jobs.push(downloadProgramDocument);
    worksheetResults.forEach((doc) => jobs.push(() => downloadWorksheetDocument(doc)));
    if (slidesResult?.downloadUrl) jobs.push(downloadSlidesDocument);
    jobs.forEach((job, i) => setTimeout(job, i * 400));
  };

  const downloadCount =
    (programResult ? 1 : 0) + worksheetResults.length + (slidesResult?.downloadUrl ? 1 : 0);

  // Drop back into the form with every answer still filled in, so a small
  // tweak doesn't mean re-entering the whole thing. Results are kept in state
  // so the teacher can return to their downloads without regenerating.
  const handleEditForm = () => {
    setIsComplete(false);
    setError(null);
    // Optional call — jsdom (and older browsers) leave scrollIntoView undefined.
    document.querySelector('.lesson-creator-results')?.scrollIntoView?.({ block: 'start' });
  };

  // ── Results screen ─────────────────────────────────────────────────────────
  if (isComplete) {
    return (
      <div className="form-container lesson-creator-results worksheet-form">
        <div className="lesson-creator-results__header">
          <h2>Your lesson is ready!</h2>
        </div>

        {followOnErrors.length > 0 && (
          <div className="lesson-creator-results__notice">
            <p>Some items couldn&apos;t be generated — you can head back and try them again:</p>
            <ul>
              {followOnErrors.map((msg, i) => (
                <li key={i}>{msg}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="lesson-creator-download-grid">
          {programResult && (
            <div className="lesson-creator-download-tile">
              <div className="lesson-creator-download-info">
                <span className="lesson-creator-download-name">
                  {programResult.documentType === 'lesson_plan' ? 'Lesson Plan' : 'Unit of Work'}
                </span>
                <span className="lesson-creator-download-meta">Editable Word document</span>
              </div>
              <button type="button" className="primary-button" onClick={downloadProgramDocument}>
                Download
              </button>
            </div>
          )}

          {slidesResult && slidesResult.downloadUrl && (
            <div className="lesson-creator-download-tile">
              <div className="lesson-creator-download-info">
                <span className="lesson-creator-download-name">Slideshow</span>
                <span className="lesson-creator-download-meta">Editable PowerPoint</span>
              </div>
              <button type="button" className="primary-button" onClick={downloadSlidesDocument}>
                Download
              </button>
            </div>
          )}

          {worksheetResults.map((doc) => (
            <div className="lesson-creator-download-tile" key={doc.documentId}>
              <div className="lesson-creator-download-info">
                <span className="lesson-creator-download-name">{doc.differentiationLevel} worksheet</span>
                <span className="lesson-creator-download-meta">
                  {includeAnswerKey ? 'Answer key included' : 'Editable Word document'}
                </span>
              </div>
              <button type="button" className="primary-button" onClick={() => downloadWorksheetDocument(doc)}>
                Download
              </button>
            </div>
          ))}
        </div>

        {downloadCount > 1 && (
          <div className="lesson-creator-download-all">
            <button type="button" className="primary-button" onClick={downloadAllResults}>
              Download all
            </button>
          </div>
        )}

        <div className="form-actions document-actions">
          <button type="button" className="secondary-button" onClick={handleEditForm}>
            Edit form
          </button>
          <button type="button" className="primary-button" onClick={onBackToDashboard}>
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const missingFields = getMissingFields();
  const isFormValid = missingFields.length === 0;

  // Block generation when a selected resource has hit its monthly limit, mirroring
  // the standalone forms. Only gate on products whose usage has actually loaded and
  // reports can_use === false; the backend stays the authoritative enforcer.
  const teachingProgramUsage = programType === 'lesson_plan' ? lessonPlanUsage : unitOfWorkUsage;
  const activeSlideshowUsage = imageSource === 'generative' ? slideshowUsage : slideshowNoImageUsage;
  const usageBlocked =
    (hasTeachingProgram && !!teachingProgramUsage && !teachingProgramUsage.can_use) ||
    (hasWorksheet && !!worksheetUsage && !worksheetUsage.can_use) ||
    (hasSlides && !!activeSlideshowUsage && !activeSlideshowUsage.can_use);

  return (
    // Reuse the compact, minimalist styling from the teaching-program and
    // worksheet forms by adopting their container scopes (these stylesheets
    // scope all their radio/checkbox/section rules under these classes).
    <div className="form-container lesson-creator-form teaching-program-form worksheet-form">
      {error && (
        <div className="form-error-message">
          <p>{error}</p>
        </div>
      )}

      {/* Editing after a finished run — the previous documents are still held in
          state, so offer them back rather than making a regenerate the only way
          to reach them again. */}
      {downloadCount > 0 && !isGenerating && (
        <div className="lesson-creator-resume">
          <p>
            Your last documents are still here. Change what you need and generate again,
            or go back to your downloads.
          </p>
          <button type="button" className="secondary-button" onClick={() => setIsComplete(true)}>
            Back to downloads
          </button>
        </div>
      )}

      <form onSubmit={(e) => { e.preventDefault(); handleGenerate(); }}>

        {/* ── Teaching program: lead with the program type choice ──────────── */}
        {hasTeachingProgram && (
          <div className="lesson-creator-section">
            <div className="lesson-creator-section__header">
              <span className="lesson-creator-section__icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                </svg>
              </span>
              <h3>Teaching program</h3>
            </div>

            <div className="form-group">
              <label>What type of program?</label>
              <div className="radio-group">
                {curriculum !== 'vic' && (
                  <label
                    className={`radio-item ${programType === 'lesson_plan' ? 'selected' : ''}`}
                    htmlFor="lc-type-lesson-plan"
                  >
                    <input
                      type="radio"
                      id="lc-type-lesson-plan"
                      name="programType"
                      value="lesson_plan"
                      checked={programType === 'lesson_plan'}
                      onChange={() => setProgramType('lesson_plan')}
                    />
                    <div className="radio-custom" />
                    <span>Single Lesson Plan</span>
                  </label>
                )}
                <label
                  className={`radio-item ${programType === 'unit_of_work' ? 'selected' : ''}`}
                  htmlFor="lc-type-unit"
                >
                  <input
                    type="radio"
                    id="lc-type-unit"
                    name="programType"
                    value="unit_of_work"
                    checked={programType === 'unit_of_work'}
                    onChange={() => setProgramType('unit_of_work')}
                  />
                  <div className="radio-custom" />
                  <span>Unit of Work</span>
                </label>
              </div>
            </div>

            {programType === 'unit_of_work' && (
              <div className="form-group" id="lc-num-lessons">
                <label htmlFor="lc-num-lessons-input">
                  How many lessons? *{' '}
                  {!canUseTenLessons && <span className="pro-badge">PRO</span>}
                </label>
                <input
                  type="number"
                  id="lc-num-lessons-input"
                  value={numberOfLessons ?? ''}
                  onChange={(e) => setNumberOfLessons(e.target.value ? parseInt(e.target.value, 10) : undefined)}
                  min="1"
                  max={maxLessons}
                  placeholder={`Create up to ${maxLessons} lessons at a time`}
                />
                {!canUseTenLessons && (
                  <span className="differentiation-upgrade">Upgrade to Gymea PRO to create up to 10 lessons.</span>
                )}
              </div>
            )}

            {programType === 'unit_of_work' && (
              <div className="form-group" id="lc-table-orientation">
                <label>Layout options</label>
                <div className="option-card-group orientation-options" role="radiogroup" aria-label="Layout options">
                  {LESSON_TABLE_ORIENTATIONS.map((option) => (
                    <label
                      key={option.value}
                      className={`option-card ${effectiveTableOrientation === option.value ? 'selected' : ''}`}
                    >
                      <input
                        type="radio"
                        name="lessonTableOrientation"
                        value={option.value}
                        checked={effectiveTableOrientation === option.value}
                        onChange={() => setTableOrientation(option.value)}
                      />
                      <span className="orientation-glyph" aria-hidden="true">{option.glyph}</span>
                      <div className="option-card-content">
                        <span className="option-card-title">{option.title}</span>
                        <span className="option-card-description">{option.description}</span>
                        {option.value === 'columns' && effectiveTableOrientation === 'columns' && (numberOfLessons ?? 0) > 5 && (
                          <span className="orientation-note">6+ lessons split across two stacked tables</span>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Slides-only: where the source lesson comes from ──────────────── */}
        {slidesPicksSource && (
          <div className="lesson-creator-section">
            <div className="lesson-creator-section__header">
              <span className="lesson-creator-section__icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                </svg>
              </span>
              <h3>Your lesson</h3>
            </div>

            <div className="form-group" id="lc-slides-source">
              <label>What are we making slides from? *</label>
              <div className="option-card-group" role="radiogroup" aria-label="Slideshow lesson source">
                <label className={`option-card ${slidesSource === 'upload' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="slidesSource"
                    value="upload"
                    checked={slidesSource === 'upload'}
                    onChange={() => setSlidesSource('upload')}
                  />
                  <div className="option-card-content">
                    <span className="option-card-title">Upload a document</span>
                    <span className="option-card-description">
                      Use a lesson plan or unit of work you already have
                    </span>
                  </div>
                </label>
                <label className={`option-card ${slidesSource === 'describe' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="slidesSource"
                    value="describe"
                    checked={slidesSource === 'describe'}
                    onChange={() => setSlidesSource('describe')}
                  />
                  <div className="option-card-content">
                    <span className="option-card-title">Describe your lesson</span>
                    <span className="option-card-description">
                      Tell us what you're teaching and we'll build the deck from scratch
                    </span>
                  </div>
                </label>
              </div>
            </div>

            {slidesNeedsUpload && (
              <div className="form-group" id="lc-slides-upload">
                <label>Upload your lesson plan or unit of work *</label>
                <input
                  ref={slidesFileInputRef}
                  type="file"
                  accept=".docx"
                  className="ws-file-input"
                  aria-label="Upload your lesson plan or unit of work"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) handleSlidesFileSelect(f); }}
                />
                <div
                  className={`upload-drop-zone ws-upload-drop-zone ${isSlidesFileDragging ? 'dragging' : ''} ${uploadedSlidesFile ? 'has-file' : ''}`}
                  onClick={() => !uploadedSlidesFile && slidesFileInputRef.current?.click()}
                  onDragOver={(e) => { e.preventDefault(); setIsSlidesFileDragging(true); }}
                  onDragLeave={() => setIsSlidesFileDragging(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setIsSlidesFileDragging(false);
                    const f = e.dataTransfer.files[0];
                    if (f) handleSlidesFileSelect(f);
                  }}
                >
                  {uploadedSlidesFile ? (
                    <div className="ws-upload-file-info">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14 2 14 8 20 8" />
                      </svg>
                      <span className="ws-upload-filename">{uploadedSlidesFile.name}</span>
                      <button
                        type="button"
                        className="ws-upload-remove"
                        onClick={(e) => {
                          e.stopPropagation();
                          setUploadedSlidesFile(null);
                          if (slidesFileInputRef.current) slidesFileInputRef.current.value = '';
                        }}
                        aria-label="Remove file"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="18" y1="6" x2="6" y2="18" />
                          <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                      </button>
                    </div>
                  ) : (
                    <div className="upload-placeholder">
                      <div className="upload-icon">
                        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                          <polyline points="17 8 12 3 7 8" />
                          <line x1="12" y1="3" x2="12" y2="15" />
                        </svg>
                      </div>
                      <p className="upload-title">Drag your document here, or click to browse</p>
                      <p className="upload-hint">.docx files only</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Shared lesson context — hidden when an uploaded document already
            carries all of it ──────────────────────────────────────────────── */}
        {needsLessonContext && (
        <div className="form-section">
          <div className="form-group" id="lc-curriculum">
            <label>Curriculum *</label>
            <div className="radio-group">
              {(['nsw', 'australian', 'vic'] as const).map((c) => (
                <label
                  key={c}
                  className={`radio-item ${curriculum === c ? 'selected' : ''}`}
                  htmlFor={`lc-curriculum-${c}`}
                >
                  <input
                    type="radio"
                    id={`lc-curriculum-${c}`}
                    name="curriculum"
                    value={c}
                    checked={curriculum === c}
                    onChange={() => handleCurriculumChange(c)}
                  />
                  <div className="radio-custom" />
                  <span>{c === 'nsw' ? 'NSW' : c === 'australian' ? 'Australian' : 'Victorian'}</span>
                </label>
              ))}
            </div>
          </div>

          {/* ── Shared: subject ──────────────────────────────────────────────── */}
          <div className="form-group" id="lc-subject">
            <label>Subject *</label>
            <div className="radio-group">
              {availableSubjects.map((s) => (
                <div
                  key={s.value}
                  className={`radio-item ${subject === s.value ? 'selected' : ''}`}
                  onClick={() => setSubject(s.value)}
                >
                  <input
                    type="radio"
                    id={`lc-subject-${s.value}`}
                    name="subject"
                    value={s.value}
                    checked={subject === s.value}
                    onChange={() => setSubject(s.value)}
                  />
                  <div className="radio-custom" />
                  <label htmlFor={`lc-subject-${s.value}`} onClick={(e) => e.stopPropagation()}>
                    {s.label}
                  </label>
                </div>
              ))}
              {availableSubjects.length === 0 && (
                <p className="helper-text">Select a curriculum to see applicable subjects.</p>
              )}
            </div>
          </div>

          {/* ── Shared: stages / year levels ─────────────────────────────────── */}
          <div className="form-group" id="lc-stages">
            <label>What {stageLabel} is this for? *</label>
            <div className={`checkbox-group ${curriculum !== 'nsw' ? 'year-level-grid' : ''}`}>
              {availableStages.map((s) => (
                <div
                  key={s.value}
                  className={`checkbox-item ${activeStages.includes(s.value) ? 'selected' : ''}`}
                  onClick={() => toggleStage(s.value)}
                >
                  <input
                    type="checkbox"
                    id={`lc-stage-${s.value}`}
                    checked={activeStages.includes(s.value)}
                    onChange={() => toggleStage(s.value)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <div className="checkbox-custom" />
                  <label htmlFor={`lc-stage-${s.value}`} onClick={(e) => e.stopPropagation()}>
                    {s.label}
                  </label>
                </div>
              ))}
              {availableStages.length === 0 && (
                <p className="helper-text">Select a curriculum to see applicable stages.</p>
              )}
            </div>
          </div>

          {/* ── VIC: capabilities ────────────────────────────────────────────── */}
          {isVIC && (
            <>
              <div className="form-group" id="lc-capabilities">
                <label>General Capabilities (1–4) *</label>
                <div className="checkbox-group">
                  {VIC_CAPABILITIES.map((c) => (
                    <div
                      key={c.value}
                      className={`checkbox-item ${capabilities.includes(c.value) ? 'selected' : ''}`}
                      onClick={() =>
                        setCapabilities((prev) =>
                          prev.includes(c.value)
                            ? prev.filter((x) => x !== c.value)
                            : prev.length < 4
                            ? [...prev, c.value]
                            : prev
                        )
                      }
                    >
                      <input
                        type="checkbox"
                        id={`lc-cap-${c.value}`}
                        checked={capabilities.includes(c.value)}
                        disabled={!capabilities.includes(c.value) && capabilities.length >= 4}
                        readOnly
                      />
                      <div className="checkbox-custom" />
                      <span>{c.label}</span>
                    </div>
                  ))}
                </div>
                <p className="helper-text">Select between 1 and 4 capabilities</p>
              </div>

              <div className="form-group" id="lc-priorities">
                <label>Cross-Curriculum Priorities (1–3) *</label>
                <div className="checkbox-group">
                  {VIC_PRIORITIES.map((p) => (
                    <div
                      key={p.value}
                      className={`checkbox-item ${crossCurriculumPriorities.includes(p.value) ? 'selected' : ''}`}
                      onClick={() =>
                        setCrossCurriculumPriorities((prev) =>
                          prev.includes(p.value)
                            ? prev.filter((x) => x !== p.value)
                            : prev.length < 3
                            ? [...prev, p.value]
                            : prev
                        )
                      }
                    >
                      <input
                        type="checkbox"
                        id={`lc-pri-${p.value}`}
                        checked={crossCurriculumPriorities.includes(p.value)}
                        disabled={!crossCurriculumPriorities.includes(p.value) && crossCurriculumPriorities.length >= 3}
                        readOnly
                      />
                      <div className="checkbox-custom" />
                      <span>{p.label}</span>
                    </div>
                  ))}
                </div>
                <p className="helper-text">Select between 1 and 3 priorities</p>
              </div>
            </>
          )}

          {/* ── Shared: outcomes ─────────────────────────────────────────────── */}
          <div className="form-group" id="lc-outcomes">
            <label htmlFor="lc-outcomes-input">What do you want to teach? *</label>
            <textarea
              id="lc-outcomes-input"
              value={outcomes}
              onChange={(e) => setOutcomes(e.target.value)}
              rows={1}
              placeholder="For example, arrays for multiplication, writing compound sentences."
            />
          </div>

          {/* ── Shared: additional info ──────────────────────────────────────── */}
          <div className="form-group">
            <label htmlFor="lc-additional-info">Do you have any custom instructions? (optional)</label>
            <textarea
              id="lc-additional-info"
              value={additionalInfo}
              onChange={(e) => setAdditionalInfo(e.target.value)}
              rows={1}
              placeholder="For example, focus on collaborative learning, include sports themes."
            />
          </div>
        </div>
        )}

        {/* ── Worksheet section ─────────────────────────────────────────────── */}
        {hasWorksheet && (
          <div className="lesson-creator-section">
            <div className="lesson-creator-section__header">
              <span className="lesson-creator-section__icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                </svg>
              </span>
              <h3>Worksheet</h3>
            </div>

            <div className="form-group" id="ws-section-type">
              <label>What kind of worksheet? *</label>
              <div className="worksheet-type-grid" role="radiogroup">
                {WORKSHEET_TYPES.map((type) => (
                  <label
                    key={type.value}
                    className={`worksheet-type-card ${worksheetType === type.value ? 'selected' : ''}`}
                  >
                    <input
                      type="radio"
                      name="worksheetType"
                      value={type.value}
                      checked={worksheetType === type.value}
                      onChange={() => handleWorksheetTypeChange(type.value)}
                    />
                    <span className="worksheet-type-text">
                      <span className="worksheet-type-label">{type.label}</span>
                      <span className="worksheet-type-hint">{type.hint}</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div className="form-group" id="lc-question-styles">
              <label>Question styles *</label>
              <div className="question-style-presets">
                {QUESTION_PRESETS.map((preset) => {
                  const isActive =
                    preset.styles.length === questionStyles.length &&
                    preset.styles.every((s) => questionStyles.includes(s)) &&
                    preset.styles.every((s) => questionCounts[s] === preset.counts[s]);
                  return (
                    <button
                      key={preset.label}
                      type="button"
                      className={`preset-chip ${isActive ? 'preset-chip--active' : ''}`}
                      onClick={() => {
                        setQuestionStyles(preset.styles);
                        setQuestionCounts({ ...preset.counts });
                      }}
                    >
                      {preset.label}
                    </button>
                  );
                })}
                <button
                  type="button"
                  className={`preset-chip ${!QUESTION_PRESETS.some(
                    (p) =>
                      p.styles.length === questionStyles.length &&
                      p.styles.every((s) => questionStyles.includes(s)) &&
                      p.styles.every((s) => questionCounts[s] === p.counts[s])
                  ) ? 'preset-chip--active' : ''}`}
                  onClick={() => { setQuestionStyles([]); setQuestionCounts({}); }}
                >
                  Make my own
                </button>
              </div>
              <div className="checkbox-group question-styles-group">
                {QUESTION_STYLES.map((style) => {
                  const isSelected = questionStyles.includes(style.value);
                  const count = questionCounts[style.value];
                  return (
                    <div key={style.value} className="question-style-row">
                      <div
                        className={`checkbox-item question-style-checkbox ${isSelected ? 'selected' : ''}`}
                        onClick={() => toggleQuestionStyle(style.value)}
                      >
                        <input
                          type="checkbox"
                          id={`lc-qs-${style.value}`}
                          checked={isSelected}
                          onChange={() => toggleQuestionStyle(style.value)}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <div className="checkbox-custom" />
                        <label htmlFor={`lc-qs-${style.value}`} onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleQuestionStyle(style.value); }}>
                          {style.label}
                        </label>
                      </div>
                      {isSelected && (
                        <div className="question-count-input">
                          <input
                            type="number"
                            value={count ?? ''}
                            onChange={(e) =>
                              setQuestionCounts((prev) => ({
                                ...prev,
                                [style.value]: parseInt(e.target.value, 10) || undefined,
                              }))
                            }
                            min="1"
                            max={style.maxQuestions}
                            placeholder="How many?"
                            onClick={(e) => e.stopPropagation()}
                          />
                          <span className="max-indicator">Max {style.maxQuestions}</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="form-group" id="lc-differentiation">
              <label>Differentiation levels *</label>
              <p className="form-hint">Core is always available. Support and Challenge are Gymea PRO.</p>
              <div className="differentiation-group">
                {DIFFERENTIATION_OPTIONS.map((option) => {
                  const isSelected = differentiationLevels.includes(option.value);
                  const isLocked = Boolean(option.featureKey && !isPublicTrial && !canUseFeature(option.featureKey));
                  return (
                    <div
                      key={option.value}
                      className={`differentiation-row ${isSelected ? 'selected' : ''} ${isLocked ? 'locked' : ''}`}
                      onClick={() => toggleDifferentiation(option.value, option.featureKey)}
                      aria-disabled={isLocked}
                    >
                      <input
                        type="checkbox"
                        id={`lc-diff-${option.value}`}
                        checked={isSelected}
                        disabled={isLocked}
                        onChange={() => toggleDifferentiation(option.value, option.featureKey)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <div className="checkbox-custom" />
                      <div className="differentiation-content">
                        <span className="differentiation-title">
                          {option.label}
                          {option.featureKey && <span className="pro-badge">PRO</span>}
                        </span>
                        <span className="differentiation-description">{option.description}</span>
                        {isLocked && (
                          <span className="differentiation-upgrade">Upgrade to Gymea PRO to unlock.</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="form-group">
              <div
                className={`checkbox-item answer-key-toggle ${includeAnswerKey ? 'selected' : ''}`}
                onClick={() => setIncludeAnswerKey((v) => !v)}
              >
                <input
                  type="checkbox"
                  id="lc-answer-key"
                  checked={includeAnswerKey}
                  onChange={(e) => setIncludeAnswerKey(e.target.checked)}
                  onClick={(e) => e.stopPropagation()}
                />
                <div className="checkbox-custom" />
                <label htmlFor="lc-answer-key" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIncludeAnswerKey((v) => !v); }}>
                  Include an answer key
                </label>
              </div>
            </div>
          </div>
        )}

        {/* ── Slides section ────────────────────────────────────────────────── */}
        {hasSlides && (
          <div className="lesson-creator-section">
            <div className="lesson-creator-section__header">
              <span className="lesson-creator-section__icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="12" rx="1" />
                  <line x1="7" y1="8" x2="17" y2="8" />
                  <line x1="7" y1="12" x2="13" y2="12" />
                  <rect x="8" y="18" width="8" height="2" rx="1" />
                  <path d="M12 16v2" />
                </svg>
              </span>
              <h3>Slideshow</h3>
            </div>

            <div className="form-group">
              <label>Choose a theme</label>
              <div className="template-grid slide-template-grid" role="radiogroup">
                {SLIDE_TEMPLATES.map((template) => (
                  <label
                    key={template.id}
                    className={`template-card ${selectedTemplate === template.id ? 'selected' : ''}`}
                  >
                    <input
                      type="radio"
                      name="slideTemplate"
                      value={template.id}
                      checked={selectedTemplate === template.id}
                      onChange={() => setSelectedTemplate(template.id)}
                    />
                    <div className="template-preview-container">
                      <img
                        className="template-preview"
                        src={template.previewImage}
                        alt={`${template.name} preview`}
                      />
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label>Image source *</label>
              <div className="option-card-group">
                <label className={`option-card ${imageSource === 'generative' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="imageSource"
                    value="generative"
                    checked={imageSource === 'generative'}
                    onChange={() => setImageSource('generative')}
                  />
                  <div className="option-card-content">
                    <span className="option-card-title">Google's Generative AI</span>
                    <span className="option-card-description">
                      Custom images to match slide content
                      {slideshowUsage?.remaining != null && ` (${slideshowUsage.remaining} remaining)`}
                    </span>
                  </div>
                </label>
                <label className={`option-card ${imageSource === 'none' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="imageSource"
                    value="none"
                    checked={imageSource === 'none'}
                    onChange={() => setImageSource('none')}
                  />
                  <div className="option-card-content">
                    <span className="option-card-title">No Images</span>
                    <span className="option-card-description">Text-only — drop in your own images</span>
                  </div>
                </label>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="lc-slides-instructions">Additional instructions (optional)</label>
              <textarea
                id="lc-slides-instructions"
                value={slidesInstructions}
                onChange={(e) => setSlidesInstructions(e.target.value)}
                rows={2}
                placeholder="E.g., Max 8 slides per lesson, use cartoon images only"
              />
            </div>
          </div>
        )}

        {/* ── Info + validation + submit ────────────────────────────────────── */}
        <div className="form-group">
          <div className="form-info-box">
            <p className="info-primary">All documents are editable Word or PowerPoint files so you can fine-tune wording, fonts, and layout.</p>
          </div>
        </div>

        <MissingFieldsSummary missingFields={missingFields} />

        <div className="form-actions">
          <button type="button" className="secondary-button" onClick={onBackToDashboard}>
            Back
          </button>
          <button
            type="submit"
            className="primary-button"
            disabled={isGenerating || !isFormValid || usageBlocked}
          >
            {isGenerating ? 'Generating…' : 'Generate'}
          </button>
        </div>

        {/* Progress shown below the action buttons so it's visible right after
            hitting Generate, without scrolling back up. One row per job — the
            worksheet and slideshow advance side by side once running. */}
        {isGenerating && (
          <div className="lesson-creator-progress">
            {(['program', 'worksheet', 'slides'] as JobKey[]).map((key) => {
              const job = jobProgress[key];
              if (!job) return null;
              return (
                <div key={key} className="lesson-creator-progress__job">
                  <div className="lesson-creator-progress__job-head">
                    <span className="lesson-creator-progress__job-label">{job.label}</span>
                    <span className="lesson-creator-progress__job-status">
                      {job.status === 'done'
                        ? 'Done'
                        : job.status === 'error'
                        ? 'Failed'
                        : `${job.percent}%`}
                    </span>
                  </div>
                  <div className="lesson-creator-progress__bar-track">
                    <div
                      className={`lesson-creator-progress__bar-fill${
                        job.status === 'done' ? ' is-done' : ''
                      }${job.status === 'error' ? ' is-error' : ''}`}
                      style={{ width: `${job.status === 'error' ? 100 : job.percent}%` }}
                    />
                  </div>
                  <p className="lesson-creator-progress__message">{job.message}</p>
                </div>
              );
            })}
          </div>
        )}

        {/* ── Usage monitors — shown underneath the Generate button, one card per
            selected document type (mirrors the personalised-plan layout) ─────── */}
        {((hasTeachingProgram && teachingProgramUsage) ||
          (hasWorksheet && worksheetUsage) ||
          (hasSlides && activeSlideshowUsage)) && (
          <div className="usage-indicators-container usage-card-group">
            <div className="usage-card-group__header">
              <span className="usage-card-group__title">Monthly usage</span>
              <span className="usage-card-group__period">Period · {usagePeriod}</span>
            </div>
            {hasTeachingProgram && teachingProgramUsage && (
              <UsageIndicator
                productName={teachingProgramUsage.name}
                used={teachingProgramUsage.used}
                limit={teachingProgramUsage.limit}
                remaining={teachingProgramUsage.remaining}
                canUse={teachingProgramUsage.can_use}
                period={usagePeriod}
                loading={usageLoading}
              />
            )}
            {hasWorksheet && worksheetUsage && (
              <UsageIndicator
                productName={worksheetUsage.name}
                used={worksheetUsage.used}
                limit={worksheetUsage.limit}
                remaining={worksheetUsage.remaining}
                canUse={worksheetUsage.can_use}
                period={usagePeriod}
                loading={usageLoading}
              />
            )}
            {hasSlides && activeSlideshowUsage && (
              <UsageIndicator
                productName={activeSlideshowUsage.name}
                used={activeSlideshowUsage.used}
                limit={activeSlideshowUsage.limit}
                remaining={activeSlideshowUsage.remaining}
                canUse={activeSlideshowUsage.can_use}
                period={usagePeriod}
                loading={usageLoading}
              />
            )}
          </div>
        )}
      </form>
    </div>
  );
};
